import React, { useState } from "react";
import { Button, Stack, CircularProgress, Dialog, Container, Typography } from "@mui/material";
import { grey, red } from "@mui/material/colors";
import TicketCommentsDialog from "../TicketCommentsDialog";
import { updateTicketStatus } from "../../../api/ticketsApi";
import { notification } from "../../../utils/Notifications";
import useLogout from '../../../hooks/auth/authHook.js';

const buttonStyles = {
  activeButton: {
    width: "65px",
    borderRadius: "0.4rem",
    height: "26px",
    backgroundColor: red["A400"],
    color: "white",
    fontSize: "12px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: red["A700"],
    },
  },
  inactiveButton: {
    width: "65px",
    borderRadius: "0.4rem",
    height: "26px",
    backgroundColor: grey[300],
    color: grey[600],
    fontSize: "12px",
    fontWeight: 500,
  },
  circularProgress: {
    color: "white",
  },
};

const confirmationDialogStyles = {
  dialog: {
    borderRadius: "16px",
  },
  title: {
    textAlign: "center",
    fontWeight: "bold",
    color: grey[800],
    marginBottom: 5,
  },
  subtitle: {
    textAlign: "center",
    color: grey[800],
    marginBottom: 16,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    padding: 0,
    marginTop: 26,
    gap: 12,
  },
  cancelButton: {
    width: "120px",
    borderRadius: "0.5rem",
    height: "38px",
    color: red["A400"],
    backgroundColor: red["50"],
  },
  confirmButton: {
    width: "160px",
    borderRadius: "0.5rem",
    height: "38px",
    backgroundColor: red["A400"],
    color: "white",
    "&:hover": {
      backgroundColor: red["A700"],
    },
  },
  mainContainer: {
    padding: 30,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
};

export default function TicketsStatusActions({ ticket, fetchTickets, currentStatus }) {
  const [logout] = useLogout();
  const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openProcesandoConfirmationDialog, setOpenProcesandoConfirmationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleStatusChange = async (newStatus, commentary = "") => {
    if (isLoading) return;

    setIsLoading(true);

    // Mapear el status a minúsculas para el API
    const apiStatus = newStatus.toLowerCase();

    updateTicketStatus(ticket.id, apiStatus, commentary)
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({
            text: `Ticket actualizado a ${newStatus}`,
            type: "success",
          });
          if (fetchTickets) {
            // Obtener el status actual del ticket para recargar los datos correctos
            const currentStatus = ticket.status?.toLowerCase() || "registrado";
            fetchTickets(currentStatus);
          }
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: response.data.status_Message || "Error al actualizar el ticket",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleProcesandoClick = () => {
    setOpenProcesandoConfirmationDialog(true);
  };

  const handleTerminadoClick = () => {
    setOpenConfirmationDialog(true);
  };

  const handleConfirmationDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setOpenConfirmationDialog(false);
  };

  const handleProcesandoConfirmationDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setOpenProcesandoConfirmationDialog(false);
  };

  const handleConfirmProcesando = () => {
    setOpenProcesandoConfirmationDialog(false);
    handleStatusChange("procesando");
  };

  const handleConfirmTerminado = () => {
    setOpenConfirmationDialog(false);
    setOpenCommentsDialog(true);
  };

  const handleCommentsConfirm = async (commentary) => {
    await handleStatusChange("terminado", commentary);
    setOpenCommentsDialog(false);
  };

  const handleCommentsDialogClose = () => {
    setOpenCommentsDialog(false);
  };

  if (!ticket) return null;

  const ticketStatus = ticket.status?.toLowerCase() || "";
  const tabStatus = currentStatus?.toLowerCase() || "";
  
  // Determinar qué botones mostrar según el tab activo
  const showBothButtons = tabStatus === "registrado";
  const showOnlyTerminar = tabStatus === "procesando";
  const showOnlyProcesar = tabStatus === "terminado";
  
  const canChangeToProcesando = ticketStatus !== "procesando" && ticketStatus !== "Procesando";
  const canChangeToTerminado = ticketStatus !== "terminado" && ticketStatus !== "Terminado";

  return (
    <Stack
      direction="row"
      spacing={1}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        gap: "2px",
        padding: "8px 0",
      }}
    >
      <Dialog
        onClose={handleConfirmationDialogClose}
        open={openConfirmationDialog}
        PaperProps={{ style: confirmationDialogStyles.dialog }}
      >
        <Container style={confirmationDialogStyles.mainContainer}>
          <Typography component="div" variant="h5" style={confirmationDialogStyles.title}>
            Confirmar acción
          </Typography>
          <Typography component="div" variant="body1" style={confirmationDialogStyles.subtitle}>
            ¿Estás seguro de que deseas terminar este ticket?
          </Typography>
          <Container style={confirmationDialogStyles.buttonsContainer}>
            <Button
              variant="standard"
              color="primary"
              onClick={handleConfirmationDialogClose}
              style={confirmationDialogStyles.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmTerminado}
              sx={confirmationDialogStyles.confirmButton}
            >
              Confirmar
            </Button>
          </Container>
        </Container>
      </Dialog>
      <Dialog
        onClose={handleProcesandoConfirmationDialogClose}
        open={openProcesandoConfirmationDialog}
        PaperProps={{ style: confirmationDialogStyles.dialog }}
      >
        <Container style={confirmationDialogStyles.mainContainer}>
          <Typography component="div" variant="h5" style={confirmationDialogStyles.title}>
            Confirmar acción
          </Typography>
          <Typography component="div" variant="body1" style={confirmationDialogStyles.subtitle}>
            ¿Estás seguro de que deseas procesar este ticket?
          </Typography>
          <Container style={confirmationDialogStyles.buttonsContainer}>
            <Button
              variant="standard"
              color="primary"
              onClick={handleProcesandoConfirmationDialogClose}
              style={confirmationDialogStyles.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmProcesando}
              sx={confirmationDialogStyles.confirmButton}
            >
              Confirmar
            </Button>
          </Container>
        </Container>
      </Dialog>
      <TicketCommentsDialog
        open={openCommentsDialog}
        onClose={handleCommentsDialogClose}
        onConfirm={handleCommentsConfirm}
      />
      {(showBothButtons || showOnlyProcesar) && (
        <Button
          variant="text"
          onClick={handleProcesandoClick}
          disabled={!canChangeToProcesando || isLoading}
          sx={
            canChangeToProcesando && !isLoading
              ? buttonStyles.activeButton
              : buttonStyles.inactiveButton
          }
          size="small"
        >
          {isLoading ? (
            <CircularProgress size={20} sx={buttonStyles.circularProgress} />
          ) : (
            "Procesar"
          )}
        </Button>
      )}
      {(showBothButtons || showOnlyTerminar) && (
        <Button
          variant="text"
          onClick={handleTerminadoClick}
          disabled={!canChangeToTerminado || isLoading}
          sx={
            canChangeToTerminado && !isLoading
              ? buttonStyles.activeButton
              : buttonStyles.inactiveButton
          }
          size="small"
        >
          Terminar
        </Button>
      )}
    </Stack>
  );
}

